import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router";
import { BasePage } from "../BasePage";
import { EmailPreferences } from "@/api/emailPreferences";
import { useRootStore } from "@/state/RootStore";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import type { TranslationKey } from "@/i18n/types";

const TOGGLES: { key: keyof EmailPreferences; labelKey: TranslationKey }[] = [
  { key: "questionReports", labelKey: "notifications.toggle_question_reports" },
  { key: "examReminders", labelKey: "notifications.toggle_exam_reminders" },
  { key: "productUpdates", labelKey: "notifications.toggle_product_updates" },
];

const PERSIST_DEBOUNCE_MS = 300;

const NotificationSettings: React.FC = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const store = useRootStore();
  const mainAppStore = store.mainAppStore;
  const prefs = mainAppStore.emailPreferences;
  const loading = mainAppStore.emailPreferencesLoading;
  const error = mainAppStore.emailPreferencesError;
  const [saveError, setSaveError] = useState(false);

  // Per-toggle debounce timers. Rapid clicks on the same switch collapse into
  // a single trailing PUT so the backend's "last write wins" matches the user's
  // last click.
  const persistTimersRef = useRef<
    Map<keyof EmailPreferences, ReturnType<typeof setTimeout>>
  >(new Map());

  const loadPrefs = useCallback(() => {
    mainAppStore.loadEmailPreferences();
  }, [mainAppStore]);

  // Refetch on mount + when the tab regains visibility. Restores server truth
  // after the user navigates away with in-flight or failed updates.
  useEffect(() => {
    loadPrefs();
    const onVisibility = () => {
      if (document.visibilityState === "visible") loadPrefs();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [loadPrefs]);

  // Flush any pending writes on unmount so navigating away mid-debounce still
  // persists the user's last click.
  useEffect(() => {
    const timers = persistTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const handleToggle = useCallback(
    (key: keyof EmailPreferences, value: boolean) => {
      setSaveError(false);
      mainAppStore.setEmailPreferenceLocal(key, value);

      const existing = persistTimersRef.current.get(key);
      if (existing) clearTimeout(existing);

      const timer = setTimeout(() => {
        persistTimersRef.current.delete(key);
        mainAppStore.persistEmailPreference(key, value).catch(() => {
          setSaveError(true);
        });
      }, PERSIST_DEBOUNCE_MS);

      persistTimersRef.current.set(key, timer);
    },
    [mainAppStore]
  );

  return (
    <BasePage activeItem="profile">
      <div className="grow py-12 flex justify-center">
        <div className="w-[594px] flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/profile")}
              aria-label={t("profile.back_to_profile_aria")}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            <h2 className="text-black text-xl font-bold font-inter">
              {t("notifications.page_title")}
            </h2>
          </div>

          {/* Email Notifications Section */}
          <div className="flex flex-col gap-4">
            <h3 className="text-[#9a9a9a] text-xs font-bold font-inter uppercase tracking-wide">
              {t("notifications.section_email")}
            </h3>

            {loading && !prefs ? (
              <p className="text-sm text-[#9a9a9a] py-3">{t("common.loading")}</p>
            ) : error && !prefs ? (
              <div className="flex flex-col items-start gap-2 py-3">
                <p className="text-sm text-red-600">
                  {t("errors.failed_load_email_prefs")}
                </p>
                <button
                  onClick={loadPrefs}
                  className="text-sm text-[#4CAF50] underline"
                >
                  {t("common.try_again")}
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {saveError && (
                  <p className="text-sm text-red-600">
                    {t("errors.failed_save_email_prefs")}
                  </p>
                )}
                {TOGGLES.map(({ key, labelKey }) => {
                  const label = t(labelKey);
                  return (
                    <div
                      key={key}
                      className="px-4 py-3 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center"
                    >
                      <span className="text-black text-sm font-normal font-inter">
                        {label}
                      </span>
                      <button
                        role="switch"
                        aria-checked={prefs?.[key] ?? true}
                        aria-label={label}
                        onClick={() => handleToggle(key, !(prefs?.[key] ?? true))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          (prefs?.[key] ?? true) ? "bg-[#4CAF50]" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            (prefs?.[key] ?? true)
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </BasePage>
  );
});

export default NotificationSettings;
